import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import AuthService from './services/AuthService';
import conversationService from './services/ConversationService';
import baseService from './services/BaseService';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, Select, MenuItem, TableHead, TableRow, Paper, Button, Snackbar, Alert, TextField, Drawer, Menu, Divider, Dialog as MuiDialog, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DialogActions, DialogContent, DialogTitle, DialogContentText } from '@mui/material';

function Conversations() {
    const [conversations, setConversations] = useState([]);
    const [bases, setBases] = useState([]);
    const [selectedBase, setSelectedBase] = useState('');

    const [selectedConversationDetails, setSelectedConversationDetails] = useState(null);
    const [activeConversationId, setActiveConversationId] = useState(null);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const { t } = useTranslation();
    const loginAs = AuthService.getLoginAs();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [loadingConversations, setLoadingConversations] = useState(false);
    // Default: last 30 days
    const [rangeFrom, setRangeFrom] = useState(dayjs().subtract(30, 'day').startOf('day'));
    const [rangeTo, setRangeTo] = useState(dayjs().endOf('day'));
    const [rangeMenuEl, setRangeMenuEl] = useState(null);
    const [openCustom, setOpenCustom] = useState(false);
    const [tempFrom, setTempFrom] = useState(rangeFrom);
    const [tempTo, setTempTo] = useState(rangeTo);

    const fetchConversations = async (s3_path) => {
        try {
            setLoadingConversations(true);
            const loginAsUri = loginAs ? `?loginAs=${loginAs}` : '';
            const s3_pathOverride = s3_path ? s3_path : selectedBase;
            const params = {};
            if (rangeFrom) params.start_date = rangeFrom.startOf('day').unix();
            if (rangeTo) params.end_date = rangeTo.endOf('day').unix();
            const data = await conversationService.getConversation(s3_pathOverride, loginAsUri, false, params);
            setConversations(data);


        } catch (error) {
            console.error('Error fetching files:', error);
        } finally {
            setLoadingConversations(false);
        }
    };

    const handleBaseChange = (event) => {
        console.log(`handleBaseChange ${event.target.value}`)
        setSelectedBase(event.target.value);
        fetchConversations(event.target.value);
        // Add any additional logic needed when the base changes
    };

    const fetchBases = async () => {
        try {
            const bases = await baseService.getBases(loginAs);
            // console.log(`Fetched Bases: ${JSON.stringify(bases)}`);
            const s3_path = bases[0]['s3_path'];
            setSelectedBase(s3_path);
            setBases(bases);

            // console.log(`calling fetch file: ${selectedBase}`);
            fetchConversations(s3_path);
            // Transient message after base selection and initial fetch
            const baseName = bases[0].base_name
            setSnackbarMessage(`${t('welcomeBar.selectedBase')}: ${baseName}`);
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Error fetching bases:', error);
        }
    };
    useEffect(() => {
        fetchBases();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const handleExport = async () => {
        if (!selectedBase) return;
        setExporting(true);
        try {
            const loginAsUri = loginAs ? `?loginAs=${loginAs}` : '';
            const params = {};
            if (rangeFrom) params.start_date = rangeFrom.startOf('day').unix();
            if (rangeTo) params.end_date = rangeTo.endOf('day').unix();
            const result = await conversationService.getConversation(selectedBase, loginAsUri, true, params);
            if (result && typeof result === 'object' && (result.blob || result.csvText)) {
                // Prepare CSV download (supports blob or text for backward compatibility)
                const blob = result.blob ?? new Blob([result.csvText], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = result.filename || 'conversations.csv';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                setSnackbarMessage(t('conversation.exportSuccess', 'Conversation CSV exported'));
                setOpenSnackbar(true);
            } else {
                console.warn('Unexpected export response format', result);
                setSnackbarMessage(t('conversation.exportFailed', 'Export failed: unexpected format'));
                setOpenSnackbar(true);
            }
        } catch (e) {
            console.error('Export error', e);
            setSnackbarMessage(t('conversation.exportFailed', 'Export failed')); // fallback text
            setOpenSnackbar(true);
        } finally {
            setExporting(false);
        }
    };


    const handleRowClick = async (conversation) => {
        try {
            console.log('Row clicked:', conversation); // Debugging log
            setActiveConversationId(conversation?.id ?? null);
            if (!selectedBase) {
                console.warn('Selected base (s3_path) is empty; cannot fetch conversation details.');
            }
            if (!conversation?.id) {
                console.warn('Missing conversation.id on row data:', conversation);
            }
            const loginAsUri = loginAs ? `?loginAs=${loginAs}` : '';
            // Backend expects s3_path and transcription_id; use selectedBase for s3_path and row id as transcription_id
            setIsDrawerOpen(true); // Open immediately to show panel while loading
            const details = await conversationService.getConversationDetails(selectedBase, conversation.id, loginAsUri);
            console.log('Fetched conversation details:', details); // Debugging log
            setSelectedConversationDetails(details);
            console.log('Drawer state set to open'); // Debugging log
        } catch (error) {
            console.error('Error fetching conversation details:', error);
        }
    };
    const handleDrawerClose = () => {
        setIsDrawerOpen(false);
    };

    const formatSessionId = (conversation) => {
        const raw = conversation?.sessionID;
        if (!raw || conversation?.platform !== 'twilio') return '';

        const matchJpMobile = raw.match(/^\+81(\d{10})$/);
        if (matchJpMobile) {
            const digits = matchJpMobile[1];
            return `+81 ${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
        }

        return raw;
    };

    return (
        <>
        <Box sx={{ p: 4 }}>
            {/* Header Section */}
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" gap={2} sx={{ flexGrow: 1, maxWidth: '100%' }}>
                    {selectedBase && (
                        <Typography variant="h5" fontWeight="bold">
                            {t('files.knowledgeBases')}:
                        </Typography>
                    )}

                    {!selectedBase && (
                        <Typography variant="h5" fontWeight="bold">
                            {t('files.noKnowledgeBases')}
                        </Typography>
                    )}

                    {selectedBase && (
                        <Select
                            value={selectedBase}
                            onChange={handleBaseChange}
                            displayEmpty
                            variant="outlined"
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-notchedOutline': {
                                    // keep default outline
                                },
                                '& .MuiOutlinedInput-input': {
                                    // for native input variants
                                    paddingTop: 1,
                                    paddingBottom: 1,
                                },
                                '& .MuiOutlinedInput-root': {
                                    height: 36,
                                    padding: 0,
                                    '& .MuiSelect-select': {
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '0 12px',
                                    },
                                },
                            }}
                        >
                            <MenuItem value="" disabled>
                                {t('files.selectBase')}
                            </MenuItem>
                            {bases.map((base) => (
                                <MenuItem key={base.s3_path} value={base.s3_path}>
                                    {base.base_name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}



                </Box>
                {/* Date Range + Export top-right */}
                <Box>
                    {/* Compact date range pill */}
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => setRangeMenuEl(e.currentTarget)}
                        sx={{ textTransform: 'none', borderRadius: 3, mr: 1 }}
                    >
                        {`${rangeFrom.format('YYYY/MM/DD')} - ${rangeTo.format('YYYY/MM/DD')}`}
                    </Button>
                    <Menu anchorEl={rangeMenuEl} open={Boolean(rangeMenuEl)} onClose={() => setRangeMenuEl(null)}>
                        <MenuItem onClick={() => { setRangeFrom(dayjs().startOf('day')); setRangeTo(dayjs().endOf('day')); setRangeMenuEl(null); fetchConversations(); }}>
                            {t('conversation.today','Today')}
                        </MenuItem>
                        <MenuItem onClick={() => { setRangeFrom(dayjs().subtract(1,'day').startOf('day')); setRangeTo(dayjs().subtract(1,'day').endOf('day')); setRangeMenuEl(null); fetchConversations(); }}>
                            {t('conversation.yesterday','Yesterday')}
                        </MenuItem>
                        <MenuItem onClick={() => { setRangeFrom(dayjs().subtract(7,'day').startOf('day')); setRangeTo(dayjs().endOf('day')); setRangeMenuEl(null); fetchConversations(); }}>
                            {t('conversation.last7','Last 7 days')}
                        </MenuItem>
                        <MenuItem onClick={() => { setRangeFrom(dayjs().subtract(14,'day').startOf('day')); setRangeTo(dayjs().endOf('day')); setRangeMenuEl(null); fetchConversations(); }}>
                            {t('conversation.last14','Last 14 days')}
                        </MenuItem>
                        <MenuItem onClick={() => { setRangeFrom(dayjs().subtract(30,'day').startOf('day')); setRangeTo(dayjs().endOf('day')); setRangeMenuEl(null); fetchConversations(); }}>
                            {t('conversation.last30','Last 30 days')}
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => { setTempFrom(rangeFrom); setTempTo(rangeTo); setOpenCustom(true); setRangeMenuEl(null); }}>
                            {t('conversation.custom','Custom')}
                        </MenuItem>
                    </Menu>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleExport}
                        disabled={!selectedBase || exporting}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            padding: '4px 12px',
                            borderRadius: 3,
                        }}
                    >
                        {exporting ? t('conversation.exporting', 'Exporting...') : t('conversation.exportCsv', 'Export CSV')}
                    </Button>
                </Box>
            </Box>



            {/* Conversations Table */}
            <TableContainer component={Paper} elevation={0} sx={{ mt: 2, borderRadius: 2 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('conversation.id', 'ID')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('conversation.phoneNumber', 'From')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200' }}>
                                {t('conversation.createdAt')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200', textAlign: 'right' }}>
                                {t('conversation.credits')}
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'grey.200', textAlign: 'right' }}>
                                {t('conversation.duration')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {conversations.map((conversation) => (
                            <Tooltip
                                key={conversation.id}
                                title={t('conversation.rowHint')}
                                arrow
                                enterDelay={800}
                                componentsProps={{
                                    tooltip: {
                                        sx: {
                                            bgcolor: 'error.light',
                                            color: 'common.white',
                                        },
                                    },
                                }}
                            >
                                <TableRow
                                    hover
                                    onClick={() => handleRowClick(conversation)}
                                    selected={activeConversationId === conversation.id}
                                    sx={{
                                        cursor: 'pointer',
                                        backgroundColor:
                                            activeConversationId === conversation.id ? 'action.selected' : 'inherit',
                                        borderLeft:
                                            activeConversationId === conversation.id
                                                ? '4px solid'
                                                : '4px solid transparent',
                                        borderLeftColor:
                                            activeConversationId === conversation.id
                                                ? 'primary.main'
                                                : 'transparent',
                                    }}
                                >
                                    <TableCell>{conversation.id}</TableCell>
                                    <TableCell>{formatSessionId(conversation)}</TableCell>
                                    <TableCell>
                                        {dayjs(conversation.createdAt).format('YYYY/MM/DD - HH:mm')}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>{conversation.credits}</TableCell>
                                    <TableCell sx={{ textAlign: 'right' }}>{conversation.duration}</TableCell>
                                </TableRow>
                            </Tooltip>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Drawer for Conversation Details */}
            <Drawer anchor="right" open={isDrawerOpen} onClose={handleDrawerClose}>
                <Box sx={{ width: 420, p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>{t('conversation.details')}</Typography>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto', pr: 1 }}>
                        {selectedConversationDetails && selectedConversationDetails.map((detail, index) => {
                            const isBot = (detail?.type || '').toLowerCase() === 'bot';
                            const rawMessage = detail?.message;
                            const displayMessage =
                                typeof rawMessage === 'string'
                                    ? rawMessage
                                    : rawMessage && typeof rawMessage === 'object' && 'label' in rawMessage
                                        ? rawMessage.label
                                        : rawMessage != null
                                            ? JSON.stringify(rawMessage)
                                            : '';
                            return (
                                <Box key={index} sx={{ display: 'flex', justifyContent: isBot ? 'flex-start' : 'flex-end' }}>
                                    <Box
                                        sx={{
                                            bgcolor: isBot ? 'grey.200' : 'primary.main',
                                            color: isBot ? 'text.primary' : 'primary.contrastText',
                                            px: 1.5,
                                            py: 1,
                                            borderRadius: 2,
                                            borderTopLeftRadius: isBot ? 0 : 8,
                                            borderTopRightRadius: isBot ? 8 : 0,
                                            maxWidth: '75%',
                                            boxShadow: 1,
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {displayMessage}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8, textAlign: isBot ? 'left' : 'right' }}>
                                            {dayjs(detail.createdAt).format('YYYY/MM/DD - HH:mm')}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Drawer>

            {/* Snackbar Notification */}
            <Snackbar
                open={openSnackbar}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="success">
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
        {/* Loading popup while fetching conversations - styled similar to Files */}
        <MuiDialog
            open={loadingConversations}
            onClose={() => {}}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    padding: 2,
                    minWidth: 320,
                    textAlign: 'center',
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 'bold' }}>
                {t('conversation.loadingTitle', 'Loading conversations')}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {t('conversation.loadingMessage', 'Waiting for response...')}
                </DialogContentText>
            </DialogContent>
        </MuiDialog>
        {/* Custom range dialog */}
        <MuiDialog open={openCustom} onClose={() => setOpenCustom(false)}>
            <DialogTitle>{t('conversation.custom','Custom')}</DialogTitle>
            <DialogContent sx={{ display: 'flex', gap: 2, p: 2 }}>
                <TextField
                    label={t('conversation.from','From')}
                    type="date"
                    size="small"
                    value={tempFrom ? tempFrom.format('YYYY-MM-DD') : ''}
                    onChange={(e) => setTempFrom(dayjs(e.target.value))}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label={t('conversation.to','To')}
                    type="date"
                    size="small"
                    value={tempTo ? tempTo.format('YYYY-MM-DD') : ''}
                    onChange={(e) => setTempTo(dayjs(e.target.value))}
                    InputLabelProps={{ shrink: true }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpenCustom(false)}>{t('files.cancel')}</Button>
                <Button
                    variant="contained"
                    onClick={() => {
                        if (tempFrom && tempTo) {
                            setRangeFrom(tempFrom.startOf('day'));
                            setRangeTo(tempTo.endOf('day'));
                            fetchConversations();
                        }
                        setOpenCustom(false);
                    }}
                >
                    {t('conversation.apply','Apply')}
                </Button>
            </DialogActions>
        </MuiDialog>
        </>
    );
}

export default Conversations;
